import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { IconButton, Button } from '../../design-system/Button';
import {
  Sun,
  Moon,
  Search,
  Plus,
  Bell,
  ChevronDown,
  Layers,
  LogOut,
  Sliders,
  Users
} from 'lucide-react';

export const TopBar = ({ onOpenSidebarMobile, setCurrentView }) => {
  const { toggleTheme, isDark } = useTheme();
  const { user, logout } = useAuth();
  const {
    workspaces,
    activeWorkspace,
    setActiveWorkspaceId,
    setIsQuickAddOpen,
    setIsCommandPaletteOpen,
    setIsCreateWorkspaceModalOpen
  } = useWorkspace();

  const [isWsMenuOpen, setIsWsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  return (
    <header
      style={{
        height: '64px',
        backgroundColor: 'var(--surface-card)',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: isDark
          ? '0 4px 16px rgba(0, 0, 0, 0.4)'
          : '0 4px 16px rgba(163, 173, 180, 0.15)'
      }}
    >
      {/* Left: Brand & Workspace Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Mobile menu trigger */}
        <button
          onClick={onOpenSidebarMobile}
          style={{
            display: 'none',
            padding: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--text-primary)',
            cursor: 'pointer'
          }}
          className="mobile-menu-btn"
          aria-label="Open navigation"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>

        {/* Logo / Brand */}
        <div
          onClick={() => setCurrentView('workspace_home')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div
            style={{
              width: '34px',
              height: '34px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--accent-container)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              boxShadow: 'var(--shadow-raised-sm)'
            }}
          >
            <Layers size={20} strokeWidth={2.5} />
          </div>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '19px',
              fontWeight: '800',
              letterSpacing: '-0.02em',
              color: 'var(--text-primary)'
            }}
          >
            Forma
          </span>
        </div>

        <span style={{ color: 'var(--border-subtle)', fontSize: '18px', fontWeight: '300' }}>/</span>

        {/* Workspace Switcher */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsWsMenuOpen(!isWsMenuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: 'var(--surface-recessed)',
              boxShadow: 'var(--shadow-recessed-sm)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: '16px', color: 'var(--accent-teal)' }}
            >
              {activeWorkspace?.icon || 'apartment'}
            </span>
            <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeWorkspace?.name}
            </span>
            <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
          </button>

          {isWsMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                width: '240px',
                backgroundColor: 'var(--surface-card)',
                boxShadow: 'var(--shadow-raised-hover)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '6px',
                zIndex: 100
              }}
              className="animate-fade-in"
            >
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'var(--text-muted)',
                  padding: '6px 10px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}
              >
                Your Workspaces
              </div>
              {workspaces.map((ws) => (
                <button
                  key={ws.id}
                  onClick={() => {
                    setActiveWorkspaceId(ws.id);
                    setIsWsMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: ws.id === activeWorkspace?.id ? 'rgba(35, 190, 179, 0.15)' : 'transparent',
                    color: ws.id === activeWorkspace?.id ? 'var(--accent-teal)' : 'var(--text-primary)',
                    fontSize: '13px',
                    fontWeight: ws.id === activeWorkspace?.id ? '600' : '450',
                    cursor: 'pointer',
                    border: 'none',
                    textAlign: 'left'
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                    {ws.icon || 'apartment'}
                  </span>
                  <div style={{ overflow: 'hidden', flex: 1 }}>
                    <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {ws.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {ws.membersCount || 1} members
                    </div>
                  </div>
                </button>
              ))}

              <div style={{ height: '1px', backgroundColor: 'var(--border-divider)', margin: '6px 4px' }} />

              <button
                onClick={() => {
                  setIsWsMenuOpen(false);
                  setIsCreateWorkspaceModalOpen(true);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: 'transparent',
                  color: 'var(--accent-teal)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  border: 'none',
                  textAlign: 'left'
                }}
              >
                <Plus size={15} />
                <span>Create Workspace</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Center: Search & Command Palette Trigger */}
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: '440px', margin: '0 24px' }}>
        <button
          onClick={() => setIsCommandPaletteOpen(true)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '7px 14px',
            backgroundColor: 'var(--surface-recessed)',
            boxShadow: 'var(--shadow-recessed-sm)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            fontSize: '13px',
            cursor: 'pointer',
            outline: 'none'
          }}
          title="Global Search & Command Palette (Ctrl+K)"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <Search size={15} style={{ color: 'var(--text-muted)' }} />
            <span>Search or jump to...</span>
          </span>
          <kbd
            style={{
              padding: '2px 6px',
              fontSize: '11px',
              fontWeight: '600',
              backgroundColor: 'var(--surface-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-secondary)'
            }}
          >
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Right: Quick Add, Notifications, Theme Toggle, User Profile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Quick Add Button */}
        <Button
          variant="primary"
          size="sm"
          icon={<Plus size={16} />}
          onClick={() => setIsQuickAddOpen(true)}
          title="Create Task (Q)"
        >
          <span className="hide-on-mobile">Create</span>
          <kbd
            style={{
              padding: '1px 5px',
              fontSize: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              borderRadius: '3px',
              color: '#ffffff',
              marginLeft: '4px'
            }}
          >
            Q
          </kbd>
        </Button>

        {/* Theme Switcher Toggle */}
        <IconButton
          icon={isDark ? <Sun size={17} color="#f98e65" /> : <Moon size={17} color="#23beb3" />}
          variant="secondary"
          size="sm"
          onClick={toggleTheme}
          title={`Switch to ${isDark ? 'Light' : 'Dark'} mode`}
        />

        {/* Notifications */}
        <IconButton
          icon={<Bell size={17} />}
          variant="secondary"
          size="sm"
          onClick={() => setCurrentView('inbox')}
          title="Inbox & Notifications"
        />

        {/* User Profile Avatar / Dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '3px',
              backgroundColor: 'var(--surface-card)',
              boxShadow: 'var(--shadow-raised-sm)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '999px',
              cursor: 'pointer',
              outline: 'none'
            }}
            title={user?.name || 'Account'}
          >
            <img
              src={user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80'}
              alt={user?.name}
              style={{ width: '28px', height: '28px', borderRadius: '999px', objectFit: 'cover' }}
            />
          </button>

          {isUserMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '210px',
                backgroundColor: 'var(--surface-card)',
                boxShadow: 'var(--shadow-raised-hover)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '8px',
                zIndex: 100
              }}
              className="animate-fade-in"
            >
              <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-divider)' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  {user?.name}
                </div>
                {user?.handle && (
                  <div style={{ fontSize: '11px', color: 'var(--accent-teal)', fontWeight: '600' }}>
                    @{user.handle}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{user?.email}</div>
              </div>

              <div style={{ padding: '4px 0' }}>
                <button
                  onClick={() => {
                    setCurrentView('members');
                    setIsUserMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <Users size={15} />
                  Members & Teams
                </button>
                <button
                  onClick={() => {
                    setCurrentView('settings');
                    setIsUserMenuOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <Sliders size={15} />
                  Settings
                </button>
                <button
                  onClick={() => {
                    logout();
                    setIsUserMenuOpen(false);
                    setCurrentView('landing');
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    fontSize: '13px',
                    color: 'var(--color-danger)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <LogOut size={15} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
